import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAuctions } from "../../contexts/AuctionsContext";
import { BidAuctionCard } from "../../components/auction/BidAuctionCard";
import { EmptyState } from "../../components/auction/FilterSection";
import { useFavoriteAuctions } from "../../lib/use-favorite-auctions";

// Only auctions that are still active (upcoming or ongoing) belong in favourites.
// Ended statuses (sold/no_sale/post-sale) must never surface here — they would
// leak final prices. Matches the "live" status group in the API status catalog.
const ACTIVE_STATUSES = new Set(["scheduled", "live", "paused"]);

export function FavoritterPage() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const { auctions, loading } = useAuctions();

  const { favorites, toggleFavorite } = useFavoriteAuctions();

  const favoriteAuctions = useMemo(
    () => auctions.filter((a) => favorites.includes(a.id) && ACTIVE_STATUSES.has(a.status)),
    [auctions, favorites],
  );

  return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 py-4 sm:py-6">
      <div className="mb-8">
        <h1
          className="text-3xl font-black text-[#1a2e35]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Favoritter
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {loading ? "Laster..." : <>Du har lagret <strong>{favoriteAuctions.length}</strong> biler</>}
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-64 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : favoriteAuctions.length === 0 ? (
        <EmptyState
          message="Ingen favoritter ennå"
          sub="Klikk på hjertet på en bil for å lagre den her, så finner du den lett igjen senere."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {favoriteAuctions.map((auction) => (
            <BidAuctionCard
              key={auction.id}
              auction={auction}
              organizationSlug={organizationSlug ?? ""}
              isFavorite
              onToggleFavorite={toggleFavorite}
            />
          ))}
        </div>
      )}
    </div>
  );
}
